// @ts-check
import { t } from 'i18next';
import { mapSensitivities } from '@/utils/compass_configuration_helper/compass_configuration_helper';
import { isVersionOutdated } from '@/utils/versionCompare';
import {
  CompassCurrentSensorSettings,
  CompassInfoRequest,
  CompassSettingsRequest,
  CompassVoltageSensorSettings,
  DeviceInfoRequest,
  DeviceLocationPermission,
  FirmwareLookupTableOnAzure,
} from '../../../../../../generated-client/models';
import { UserInfo } from '../UserDashboard';
import type { OrgUser } from '@/models/User/OrgUser';
import type { PvdConfigurationOnAzure, EmergencyConfigurationOnAzure } from '@/../generated-client/models';
import { StringKeys } from '@/resources/strings/StringKeys';
/**
 * Process compass and device info into a standardized format
 * @param  compassInfo - Compass hardware information
 * @param  compassSettings - Compass sensor settings
 * @param  orgConfig - Device information
 * @returns Processed device data
 */
export const processDeviceData = (
  compassInfo: CompassInfoRequest | undefined,
  compassSettings: CompassSettingsRequest | undefined,
) => ({
  deviceName: compassInfo?.hardware_version || 'N/A',
  firmwareVersion: compassInfo?.firmware_revision || '--',
  batteryLevel: compassInfo?.battery_percentage ?? '--',
  macAddress: compassInfo?.mac_address || '--',
  compassAlertSound: compassInfo?.compass_alert_mute_status ?? '--',
  currentSensitivity: formatSensitivity(
    compassSettings?.compass_current_sensor_settings,
    compassSettings?.adaptive_shared_auto_adapt_enabled,
  ),
  voltageSensitivity: formatSensitivity(
    compassSettings?.compass_voltage_sensor_settings,
    compassSettings?.adaptive_shared_auto_adapt_enabled,
  ),
});

/**
 * Process mobile device info into a standardized format
 * @param  deviceInfo - Mobile device information
 * @returns Processed mobile data
 */
export const processMobileData = (
  deviceInfo: DeviceInfoRequest | null | undefined,
) => ({
  deviceName: deviceInfo?.model || 'N/A',
  version: deviceInfo?.os_version || 'N/A',
  batteryLevel: deviceInfo?.battery_percentage ?? 'N/A',
  manufacturer: deviceInfo?.manufacturer || 'N/A',
  model: deviceInfo?.model || 'N/A',
  locationPermissions: formatLocationPermissions(
    deviceInfo?.device_location_permission,
  ),
  notificationsPermissions: formatNotificationPermissions(
    deviceInfo?.all_notifications_enabled ? true : false,
  ),
  os: deviceInfo?.os || 'N/A',
  osVersion: deviceInfo?.os_version || 'N/A',
  osDisplay:
    deviceInfo?.os && deviceInfo?.os_version
      ? `${deviceInfo.os} ${deviceInfo.os_version}`
      : 'N/A',
  appVersion: deviceInfo?.app_version || 'N/A',
});

/**
 * Process emergency settings into a standardized format
 * @param compassSettings - Compass settings containing emergency configurations
 * @returns Processed emergency settings
 */
export const processEmergencySettings = (
  compassSettings: CompassSettingsRequest,
) => ({
  arcFlashDetection: compassSettings?.arc_flash_detection_on ?? '--',
  muteSosButton: compassSettings?.mute_sos_button ?? '--',
  fallDetection: compassSettings?.fall_detection_on ?? '--',
  headImpact: compassSettings?.head_impact_detection_on ?? '--',
});

/**
 * Get the current configuration name from organization configurations
 * @param  organizationConfigurations - Available configurations
 * @param userInfo - User information containing orgSpecAzureId
 * @returns Configuration name or 'Default'
 */
export const getCurrentConfiguration = (
  organizationConfigurations: Array<PvdConfigurationOnAzure>,
  userInfo: UserInfo,
): string => {
  if (!organizationConfigurations || !userInfo?.pvdConfigId) {
    return 'Unknown';
  }

  const assignedConfig = organizationConfigurations.find(
    (config) => config.id === userInfo.pvdConfigId,
  );

  return assignedConfig?.spec_name || 'N/A';
};

/**
 * Format one channel's last-ping sensitivity. Adaptive Modes is per channel;
 * Smart Adaptive vs Work Zone Adapt is the shared Auto Adapt flag.
 */
const formatSensitivity = (
  settings:
    | CompassCurrentSensorSettings
    | CompassVoltageSensorSettings
    | null
    | undefined,
  adaptiveSharedAutoAdaptEnabled: boolean | null | undefined,
): string => {
  if (!settings) return '--';

  if (settings.sensitivity_disabled) return t(StringKeys.DISABLED);

  if (settings.sensitivity_smart_adaptive_on) {
    if (adaptiveSharedAutoAdaptEnabled === false) {
      return t(StringKeys.WORK_ZONE_ADAPT);
    }
    return t(StringKeys.SMART_ADAPTIVE);
  }

  if (!Number.isFinite(settings.sensitivity)) {
    return '--';
  }

  return String(settings.sensitivity + 1);
};


/**
 * Format location permissions into human-readable text
 * @param  permission - Location permission value
 * @returns  - One of 'all_the_time', 'while_using_app', or 'Unknown'
 */
const formatLocationPermissions = (
  permission: DeviceLocationPermission | undefined,
) => {
  if (permission === 'all_the_time') return 'all_the_time';
  if (permission === 'while_using_app') return 'while_using_app';
  return 'N/A';
};

/**
 * Format notification permissions into human-readable text
 * @param {boolean|null} enabled - Notification enabled status
 * @returns {string} - 'enabled', 'disabled', or 'Unknown'
 */
const formatNotificationPermissions = (enabled: boolean | null) => {
  if (enabled == null) return 'N/A';
  return enabled ? 'enabled' : 'disabled';
};

/**
 * Compare mobile app version (from deviceInfo) with the latest app version for that OS.
 * @param  deviceInfo - expects { os: string, app_version: string }
 * @param  firmwareLookup - expects { latest_app_version_ios, latest_app_version_android }
 * @returns {{ appOutdated: boolean }}
 */
export function getAppStatus(
  deviceInfo: DeviceInfoRequest | undefined,
  firmwareLookup: FirmwareLookupTableOnAzure | null,
): { appOutdated: boolean } {
  if (!deviceInfo || !firmwareLookup) return { appOutdated: false };

  const operatingSystem = String(deviceInfo.os || '')
    .trim()
    .toLowerCase();
  const currentAppVersion = deviceInfo.app_version || null;

  const latestAppVersion = operatingSystem.includes('ios')
    ? (firmwareLookup.latest_app_version_ios ?? null)
    : operatingSystem.includes('android')
      ? (firmwareLookup.latest_app_version_android ?? null)
      : null;

  return {
    appOutdated: isVersionOutdated(currentAppVersion, latestAppVersion),
  };
}

/**
 * Compare device firmware version (from compassInfo) with the latest firmware version.
 * @param  compassInfo - expects { firmware_revision: string }
 * @param  firmwareLookup - expects { latest_firmware_version }
 * @returns {{ firmwareOutdated: boolean }}
 */
export function getFirmwareStatus(
  compassInfo: CompassInfoRequest | undefined,
  firmwareLookup: FirmwareLookupTableOnAzure,
): { firmwareOutdated: boolean } {
  if (!compassInfo || !firmwareLookup) return { firmwareOutdated: false };

  const currentFirmwareVersion = compassInfo.firmware_revision || null;
  const latestFirmwareVersion = firmwareLookup.latest_firmware_version ?? null;

  return {
    firmwareOutdated: isVersionOutdated(
      currentFirmwareVersion,
      latestFirmwareVersion,
    ),
  };
}

export const findUserPvdConfig = (
  user: OrgUser,
  configs: PvdConfigurationOnAzure[] | undefined,
): PvdConfigurationOnAzure | undefined =>
  configs?.find((c) => c.id === user.pvd_config_id);

export const findUserEmergencyConfig = (
  user: OrgUser,
  configs: EmergencyConfigurationOnAzure[] | undefined,
): EmergencyConfigurationOnAzure | undefined =>
  configs?.find((c) => c.id === user.emergency_config_id);
